/** @jsx jsx */
import { jsx } from "frontend/src/utils/jsx"
import { CMSLink } from "frontend/src/components/CMSLink"
import { Flex } from "frontend/src/components/Layout"
import BlockContent from "@sanity/block-content-to-react"
import { theme } from "frontend/src/utils/theme"
import { FaDownload } from "react-icons/fa"

const Nbsp = () => "\u00A0"

const styleObject = Object.keys(theme.text).reduce((acc, curr) => {
  const obj = { ...acc }
  const variant = `text.${curr}`
  const regExp = /^(new)?[hH]{1}[123456]{1}$/
  const Element = regExp.test(curr)
    ? curr.startsWith("new")
      ? curr.slice(3).toLowerCase()
      : curr.toLowerCase()
    : "p"
  obj[curr] = { variant, Element }
  return obj
}, {})

const serializers = (customStyle, as = undefined) => ({
  marks: {
    link: ({ mark: { _id, _key, ...link }, children }) => {
      return (
        <CMSLink link={link} sx={{ variant: "link.body1", ...customStyle }}>
          {children}
        </CMSLink>
      )
    },
    color: ({ mark: { _id, _key, ...markDefs }, children }) => {
      return <span style={{ ...customStyle, color: markDefs.selected }}>{children}</span>
    },
    uppercase: ({ children }) => {
      return <span style={{ ...customStyle, textTransform: "uppercase" }}>{children}</span>
    },
  },
  types: {
    block: ({ node: { style }, children }) => {
      if (as) {
        const Style = as
        return <Style sx={{ variant: "text.body1", ...customStyle }}>{children}</Style>
      } else if (style === "normal") {
        return <p sx={{ variant: "text.body1", ...customStyle }}>{children}</p>
      } else {
        const styleDefinition = styleObject[style] || {}
        const { variant = "text.body1", Element = "p" } = styleDefinition
        return <Element sx={{ variant, ...customStyle }}>{children}</Element>
      }
    },
    verticalMargin({ node: { mobile, tablet, desktop, widescreen } }) {
      const margins = [mobile, tablet, desktop, widescreen].map(item => `${item}px`)
      return (
        <div sx={{ width: "100%", height: margins }}>
          <Nbsp />
        </div>
      )
    },
    singleFile({
      node: {
        fileName,
        file: {
          asset: { url },
        },
      },
    }) {
      return (
        <Flex.row align="center" justify="space-between" sx={{ width: "100%" }}>
          <div>
            <FaDownload sx={{ mr: 3 }} />
            <span sx={{ variant: "text.body1" }}>{fileName}</span>
          </div>
          <CMSLink
            link={{ externalUrl: url, newtab: true }}
            sx={{
              py: 5,
              px: 7,
              textAlign: "center",
              bg: "almostBlack",
              color: "white",
              fontFamily: "heading",
              textTransform: "uppercase",
              fontSize: "16px",
              lineHeight: "16px",
              fontWeight: "light",
            }}
          >
            Herunterladen
          </CMSLink>
        </Flex.row>
      )
    },
  },
  container: ({ children }) => <div children={children} sx={{ ...customStyle }} />,
  list: ({ children, type }) => {
    switch (type) {
      case "bullet":
        return <ul sx={{ variant: "text.body1" }}>{children}</ul>
      case "number":
        return <ol sx={{ variant: "text.body1" }}>{children}</ol>
      default:
        return <ul sx={{ variant: "text.body1" }}>{children}</ul>
    }
  },
  listItem: ({ node, children }) => {
    return (
      <li sx={{ variant: "text.body1", ml: 7, ...customStyle, display: "list-item" }}>
        {children}
      </li>
    )
  },
})

const BlockText = ({ blocks, style, as }) => {
  return (
    <BlockContent
      projectId={process.env.GATSBY_SANITY_PROJECT}
      dataset={process.env.GATSBY_SANITY_DATASET}
      blocks={blocks}
      serializers={serializers(style, as)}
    />
  )
}

export { BlockText }
